import { Mic, MessageSquare, Target, Zap, Sparkles, FolderOpen, ExternalLink } from "lucide-react";

// Read-only Sales Critique block for the Lead Cockpit.
// Consumes lead.latest_critique (populated by /leader/leads/{id}).
// Empty state gracefully guides toward the Amanda pipeline.

const DIMENSION_META = {
  tone: { label: "Tone / Empathy", icon: MessageSquare, accent: "#60A5FA" },
  objection: { label: "Objection Handling", icon: Target, accent: "#F5C842" },
  closing: { label: "Closing Strength", icon: Zap, accent: "#10B981" },
};

function scoreColor(score) {
  if (score >= 8) return "#10B981";
  if (score >= 6.5) return "#F5C842";
  return "#F43F5E";
}

export default function SalesCritique({ lead }) {
  const critique = lead?.latest_critique;
  const folderId = lead?.cloud_folder_id;

  if (!critique) {
    return (
      <section
        className="glass-strong rounded-sm p-6 sm:p-8"
        data-testid="sales-critique-empty"
      >
        <div className="hud-label mb-3 flex items-center gap-2">
          <Sparkles size={12} className="text-[#A78BFA]" />
          Amanda · Sales Critique
        </div>
        <div className="text-slate-300 text-sm leading-relaxed">
          No transcript analysed yet for this lead. As soon as a Plaud NotePin
          recording or an SMS/voice note lands in the Cloud folder, Amanda will
          post a 3-point critique here — Tone, Objection Handling, and Closing
          Strength.
        </div>
        <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-mono-aviation text-slate-500">
          <Mic size={11} className="text-[#A78BFA]" />
          Waiting on transcript · Passive Audio Sync ready
        </div>
      </section>
    );
  }

  const overall = Number(critique.overall_score || 0);
  const overallColor = scoreColor(overall);
  const analyzedAt = critique.analyzed_at
    ? new Date(critique.analyzed_at).toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <section
      className="glass-strong rounded-sm p-6 sm:p-8 relative overflow-hidden"
      data-testid="sales-critique"
      style={{
        borderTop: "1px solid rgba(212,175,55,0.4)",
      }}
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)",
        }}
      />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="hud-label mb-2 flex items-center gap-2">
            <Sparkles size={12} className="text-gold" />
            Amanda · Sales Critique
          </div>
          <div className="font-display text-2xl text-white leading-tight">
            3-Point Coaching from the last recording
          </div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono-aviation mt-1.5 flex items-center gap-2">
            {critique.source || "Amanda transcript"}
            {analyzedAt && <span>· {analyzedAt}</span>}
          </div>
        </div>

        {/* Overall score gauge */}
        <div
          className="rounded-sm border px-4 py-3 flex items-center gap-3 shrink-0"
          style={{
            borderColor: `${overallColor}55`,
            background: `${overallColor}12`,
          }}
          data-testid="sales-critique-overall"
        >
          <div>
            <div className="text-[9px] uppercase tracking-[0.28em] text-slate-500">
              Overall
            </div>
            <div
              className="font-display text-3xl leading-none"
              style={{ color: overallColor }}
            >
              {overall.toFixed(1)}
            </div>
          </div>
          <div className="text-[10px] text-slate-500">/ 10</div>
        </div>
      </div>

      {/* Transcript snippet */}
      {critique.transcript_snippet && (
        <div
          className="rounded-sm border p-4 mb-6 relative"
          style={{
            borderColor: "rgba(167,139,250,0.35)",
            background: "rgba(167,139,250,0.06)",
          }}
          data-testid="sales-critique-snippet"
        >
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#A78BFA] mb-1.5 flex items-center gap-1.5">
            <Mic size={11} /> Transcript · Excerpt
          </div>
          <blockquote className="text-slate-200 text-sm italic leading-relaxed">
            {critique.transcript_snippet}
          </blockquote>
        </div>
      )}

      {/* 3-dimension breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["tone", "objection", "closing"].map((key) => {
          const meta = DIMENSION_META[key];
          const dim = critique[key];
          if (!dim) return null;
          const Icon = meta.icon;
          const color = scoreColor(dim.score);
          return (
            <div
              key={key}
              className="rounded-sm border p-4 relative"
              style={{
                borderColor: `${meta.accent}33`,
                background: "rgba(6,11,20,0.5)",
              }}
              data-testid={`sales-critique-${key}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-sm border flex items-center justify-center shrink-0"
                    style={{
                      borderColor: `${meta.accent}55`,
                      background: `${meta.accent}16`,
                    }}
                  >
                    <Icon size={13} style={{ color: meta.accent }} />
                  </div>
                  <div className="font-display text-sm text-white leading-tight">
                    {meta.label}
                  </div>
                </div>
                <div
                  className="font-display text-lg leading-none shrink-0"
                  style={{ color }}
                  data-testid={`sales-critique-${key}-score`}
                >
                  {Number(dim.score).toFixed(1)}
                </div>
              </div>

              {/* Score bar */}
              <div className="h-1 rounded-full overflow-hidden bg-white/5 mb-3">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, dim.score * 10)}%`,
                    background: `linear-gradient(90deg, ${color}66, ${color})`,
                  }}
                />
              </div>

              <div className="text-[12px] text-slate-300 leading-relaxed">
                {dim.text}
              </div>
            </div>
          );
        })}
      </div>

      {/* Cloud folder link, if any */}
      {folderId && (
        <div className="mt-6 flex items-center gap-3">
          <a
            href={`https://drive.google.com/drive/folders/${folderId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost-gold px-4 py-2 rounded-sm text-xs inline-flex items-center gap-2"
            data-testid="sales-critique-open-folder"
          >
            <FolderOpen size={13} /> Open Cloud Folder <ExternalLink size={11} />
          </a>
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono-aviation">
            Full transcript & audio archived in Drive
          </span>
        </div>
      )}
    </section>
  );
}
